import {
  TenantSwitcher,
  useChurchSignInAddresses,
  useEntitlements,
  useHandoff,
  useMyMemberships,
  useOnChurchHost,
  useOpenChurchApp,
  usePermissions,
  useSession,
  useTenant,
} from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { notifications } from "@mantine/notifications";
import { IconArrowsExchange, IconLayoutDashboard, IconShieldLock } from "@tabler/icons-react";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { addressSwitch } from "@how/tenancy";
import { AppFrame, ShellBrand, type AppFrameAccountLink } from "@how/ui";
import { AskBusySharingOnce } from "../features/my-schedule/BusySharing";
import { useStoreOptions } from "../features/shop/shop-queries";
import { useOwnProfile, useTenantRecord } from "../features/tenant/queries";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import { NAV } from "./nav";

/** The member portal shell: AppFrame with the church name, the tenant switcher, and permission-gated navigation. */
export function Shell() {
  const session = useSession();
  const tenant = useTenantRecord();
  const profile = useOwnProfile();
  const entitlements = useEntitlements();
  const permissions = usePermissions(
    NAV.filter((item) => item.permission !== undefined).map((item) => ({
      permission: item.permission as NonNullable<typeof item.permission>,
      ...(item.module === undefined ? {} : { module: item.module }),
    })),
  );
  // A church with no store has no Shop entry (ADR 0047 step 4); one on Shopify keeps it, pointing there.
  const storeOptions = useStoreOptions(entitlements.data?.has("commerce") === true);
  // "Open the staff dashboard": only for someone with a staff-side role in this church (issue_handoff checks again).
  const memberships = useMyMemberships();
  const resolved = useTenant();
  const current = resolved.status === "resolved" ? resolved.tenant : null;
  const staffSide =
    current !== null &&
    memberships.data?.some(
      (m) => m.tenantId === current.tenantId && m.membershipStatus === "active" && m.hasStaffAccess,
    ) === true;
  // The church's live addresses and its preference (R9, R10); HoW's address alone until they are read.
  const addresses = useChurchSignInAddresses(current?.tenantId ?? null);
  const onChurchHost = useOnChurchHost();
  const { pathname, search } = useLocation();
  const handoff = useHandoff();
  const openApp = useOpenChurchApp(env);
  // One notice per failure: each hand-off has its own effect, keyed on its own error, so a second, different
  // failure is told too.
  useEffect(() => {
    if (handoff.error !== null) showOpenFailed();
  }, [handoff.error]);
  useEffect(() => {
    if (openApp.error !== null) showOpenFailed();
  }, [openApp.error]);
  const email = session.status === "signed_in" ? (session.session.user.email ?? "") : "";
  const items = NAV.filter(
    (item) =>
      (item.permission === undefined || permissions.allowed.has(item.permission)) &&
      (item.module === undefined || entitlements.data?.has(item.module) === true) &&
      !(item.to === "/shop" && storeOptions.data?.mode === "none"),
  );
  // HoW's own link to the dashboard follows the church's preferred address (R9, ADR 0067 A8): the church's own dash.
  // address when preferred and live, otherwise the staff door at /<slug>/ (ruling H7: whatever the forward setting).
  const staffLinks: AppFrameAccountLink[] =
    current === null || !staffSide
      ? []
      : [
          {
            label: "Open the staff dashboard",
            icon: IconLayoutDashboard,
            onSelect: () => {
              openApp.open({
                tenantId: current.tenantId,
                slug: current.slug,
                app: "staff_dashboard",
                path: "/",
              });
            },
          },
        ];
  // "Open at …" (R10): this page on the portal's other address, only by a click, through the hand-off.
  const move =
    current === null
      ? null
      : addressSwitch(
          addresses,
          current.slug,
          "member_portal",
          env.VITE_HOW_PLATFORM_BASE_DOMAIN,
          onChurchHost,
          `${pathname}${search}`,
        );
  const moveLinks: AppFrameAccountLink[] =
    move === null
      ? []
      : [
          {
            label: move.label,
            ...(move.description === null ? {} : { description: move.description }),
            icon: IconArrowsExchange,
            onSelect: () => {
              handoff.open(move.target.host, move.target.path);
            },
          },
        ];
  return (
    <>
      <AppFrame
        brand={<ShellBrand surface="Members" churchName={tenant.data?.name} />}
        switcher={<TenantSwitcher env={env} surface="member_portal" />}
        nav={items}
        account={{
          email,
          ...(profile.data?.display_name ? { name: profile.data.display_name } : {}),
          ...(tenant.data === undefined ? {} : { label: tenant.data.name }),
          // HoW's help center on the platform's own domain, opened at the members' articles (ADR 0059).
          helpUrl: `https://${env.VITE_HOW_PLATFORM_BASE_DOMAIN}/help/for/members`,
          links: [
            ...staffLinks,
            ...moveLinks,
            { to: "/security", label: "Security", icon: IconShieldLock },
          ],
          onSignOut: () => {
            void supabase.auth.signOut();
          },
        }}
      />
      {/* "The portal asks once" (spec piece 3): on whichever page the person opens first (ADR 0070 R7). */}
      <AskBusySharingOnce />
    </>
  );
}

function showOpenFailed(): void {
  notifications.show({
    color: semanticColor("danger"),
    title: "Could not open that address",
    message: "That address could not be opened. Try again.",
  });
}
